import Logo from '@/assets/images/logo.png';
import { PRODUCT_ID_PREFIX } from '@/common/common.contants';
import {
  CustomSearchServiceHits,
  Product,
  ProductCardProps,
  ProductDocument,
  ProductItemForSlug,
  QuantityLimitedProduct,
} from '@/types';
import {
  ApiRecommendationType,
  RecommendationTitle,
  RecommendationType,
} from '@/types/recommendation.types';

import { AvailableLanguageTypes } from './common';

import { t, TFunction } from 'i18next';

export const getProductSearchResultFromTypeSense = (
  hits: CustomSearchServiceHits[],
  locale: string
): Product[] => {
  const getLocalizationValue = (
    locale: string,
    defaultValue: string,
    enValue?: string
  ) => {
    return locale === AvailableLanguageTypes.ENGLISH
      ? enValue || defaultValue
      : defaultValue;
  };

  const result = hits.map((hit) => {
    const document = hit.document;
    const isDiscountBadge = getDiscountDateBadge(
      document.price,
      document.specialPrice,
      document?.specialPriceFromDate,
      document?.specialPriceToDate
    );
    return {
      id: `${PRODUCT_ID_PREFIX}${document.productId}`,
      name: getLocalizationValue(locale, document.title, document.titleEn),
      originalPrice: isDiscountBadge ? document?.specialPrice : document.price,
      price: document?.specialPrice != null ? document?.price : undefined,
      defaultCartValue: document.price,
      image: String(getThumbnail(document) ?? ''),
      inStock: getInStock(document, hit),
      maxQuantityOfProduct: document.maxQuantityOfProduct,
      itemMeasurementUnit: document.itemMeasurementUnit,
      weight: document.weight,
      itemSoldBy: document.itemSoldBy,
      quantity: document.quantity,
      sku: document.sku,
      isDiscountBadge,
    } as Product;
  });

  return result;
};

export function extractProductId(gid: string): string {
  const lastSlashIndex = gid.lastIndexOf('/');
  return gid.substring(lastSlashIndex + 1);
}

export const getRecommendationType = (
  input?: string | string[]
): RecommendationType | undefined => {
  if (!input) return undefined;

  const value = Array.isArray(input) ? input[0] : input;

  if (value in RecommendationType) {
    return RecommendationType[value as keyof typeof RecommendationType];
  }

  const valueMatch = Object.values(RecommendationType).find((v) => v === value);

  return valueMatch;
};

export const getRecommendationTitle = (
  type: RecommendationType | undefined,
  recommendationType: string | undefined,
  brandName?: string
): string => {
  switch (type) {
    case RecommendationType.ADS:
      return RecommendationTitle.TOP_PICKS_FOR_YOU;
    case RecommendationType.AGEING:
      return RecommendationTitle.TOP_PICKS_FOR_YOU;
    case RecommendationType.BASKET:
      return RecommendationTitle.FREQUENTLY_BOUGHT;
    case RecommendationType.BOUGHT_TOGETHER:
      return RecommendationTitle.BOUGHT_TOGETHER;
    case RecommendationType.BRANDS:
      return t('productList.brandRecommendationTitle', { brand: brandName });
    case RecommendationType.CATEGORIES:
      return RecommendationTitle.CATEGORY;
    case RecommendationType.FRESHFOOD:
      return RecommendationTitle.FRESH_FOOD;
    case RecommendationType.NEW_ARRIVALS:
      return RecommendationTitle.TOP_PICKS_FOR_YOU;
    case RecommendationType.RECOMMENDED_FOR_YOU:
      return RecommendationTitle.RECOMMENDED_FOR_YOU;
    case RecommendationType.SEARCH_START_TOPPICKS:
      return RecommendationTitle.TOP_PICKS_FOR_YOU;
    case RecommendationType.TOPPICKS:
      return RecommendationTitle.TOP_PICKS_FOR_YOU;
    default:
      return RecommendationTitle.TOP_PICKS_FOR_YOU;
  }
};

export const mapRecommendationProductsToProductCards = (
  products: ProductDocument[],
  t: TFunction
): ProductCardProps[] => {
  return products.map((item) => {
    const isDiscountBadge = getDiscountDateBadge(
      item.price,
      item.specialPrice,
      item.specialPriceFromDate,
      item.specialPriceToDate
    );

    const discountPercent = getDiscountPercent(item.price, item.specialPrice);

    return {
      id: item.id,
      name: String(item.title ?? ''),
      title: String(item.title ?? ''),
      image: {
        src: String(getThumbnail(item) ?? ''),
        alt: String(item.title ?? 'Product'),
      },
      sameDay: false,
      deliveryTitle: '',
      price: isDiscountBadge ? item.specialPrice : item.price,
      oldPrice: item.specialPrice != null ? item.price : undefined,
      isDiscountBadge,
      discountLabel: isDiscountBadge
        ? t('productCard.discountLabel')
        : undefined,
      discountPercent,
      promotionalLabel: getPromotionLabels(item),
      itemMeasurementUnit: item.itemMeasurementUnit,
      weight: item.weight,
      itemSoldBy: item.itemSoldBy,
      inStock: !getInStock(item),
      quantity: typeof item.quantity === 'number' ? item.quantity : undefined,
      maxQuantityOfProduct: item.maxQuantityOfProduct,
      sku:item.sku
    };
  });
};

export const mapPlpProductsToProductCards = (
  products: Array<{
    id: string;
    name?: string | null;
    urlKey?: string | null;
    thumbnail?: string | null;
    deliveryType?: string[] | string | null;
    price?: number | null;
    oldPrice?: number | null;
    specialPrice?: number | null;
    isDiscountBadge?: boolean | null;
    discountPercent?: number | null;
    promotionLabel?: string[] | string | null;
    inStock?: boolean;
    itemMeasurementUnit?: string;
    weight?: number;
    itemSoldBy?: string;
    quantity?: number;
    maxQuantityOfProduct?: number;
    sku?: string;
  }>,
  t: TFunction
): ProductCardProps[] => {
  return products.map((item) => {
    const sameDay = Array.isArray(item.deliveryType)
      ? item.deliveryType.includes('same_day')
      : item.deliveryType?.includes('same_day');
    const isDiscountBadge = Boolean(item.isDiscountBadge);

    return {
      id: item.id,
      name: String(item.name ?? ''),
      urlKey: item.urlKey ?? undefined,
      title: String(item.name ?? ''),
      image: {
        src: String(item.thumbnail ?? ''),
        alt: String(item.name ?? ''),
      },
      sameDay,
      deliveryTitle: sameDay ? t('productCard.deliverySameDay') : '',
      price: item.price ?? undefined,
      oldPrice: item.oldPrice ?? undefined,
      isDiscountBadge,
      discountLabel: isDiscountBadge
        ? t('productCard.discountLabel')
        : undefined,
      discountPercent:
        typeof item.discountPercent === 'number'
          ? Math.round(item.discountPercent)
          : undefined,
      promotionalLabel: item.promotionLabel ?? '',
      inStock: item.inStock,
      itemMeasurementUnit: item.itemMeasurementUnit,
      weight: item.weight,
      itemSoldBy: item.itemSoldBy,
      quantity: item.quantity,
      maxQuantityOfProduct: item.maxQuantityOfProduct,
      sku: item.sku,
    };
  });
};

export const mapCuratedHitsToProductCards = (
  curatedHits: CustomSearchServiceHits[]
): ProductCardProps[] => {
  if (!curatedHits?.length) return [];

  return curatedHits.flatMap((hit) => {
    const doc = hit.document;
    if (!doc) return [];

    const inStock = getInStock(doc, hit);
    const hasQuantity = doc.quantity !== 0;

    if (!inStock || !hasQuantity) return [];

    const isDiscountBadge = getDiscountDateBadge(
      doc.price,
      doc.specialPrice,
      doc?.specialPriceFromDate,
      doc?.specialPriceToDate
    );
    const discountPercent = getDiscountPercent(doc.price, doc.specialPrice);

    return [
      {
        id: doc.id ?? '',
        image: {
          src: String(getThumbnail(doc) ?? ''),
          alt: String(doc.title ?? 'Product'),
        },
        title: doc.title ?? '',
        info: undefined,
        sameDay: false,
        price: isDiscountBadge ? doc.specialPrice : doc.price,
        oldPrice: doc.specialPrice != null ? doc.price : undefined,
        discountPercent,
        isDiscountBadge,
        promotionalLabel: doc.eligibleForPromotion ? 'Promotion' : undefined,
        inStock: !inStock,
        weight: doc.weight,
        itemSoldBy: doc.itemSoldBy,
        quantity: doc.quantity,
        maxQuantityOfProduct: doc.maxQuantityOfProduct,
        sku: doc.sku
      },
    ];
  });
};

const toSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
};

export function productItemToSlug(item: ProductItemForSlug) {
  const slugPart = item.urlKey || toSlug(item.name || '');
  const idPart = item.id.replace(PRODUCT_ID_PREFIX, '');
  const product_slug = `${slugPart}-${idPart}`;
  return product_slug;
}

const getDiscountPercent = (
  price: number | undefined | null,
  specialPrice: number | undefined | null
): number | undefined => {
  if (price && specialPrice != null && specialPrice < price) {
    return Math.round(((price - specialPrice) / price) * 100);
  }
  return undefined;
};

const getDateRange = (
  from: number | undefined | null,
  to: number | undefined | null
): boolean => {
  if (from == null || to == null) return true;

  const now = Date.now();
  return now >= from && now <= to;
};

const getDiscountDateBadge = (
  price: number | undefined,
  specialPrice: number | undefined,
  fromDate: number | undefined,
  toDate: number | undefined
): boolean => {
  return Boolean(
    specialPrice != null &&
    price != null &&
    specialPrice < price &&
    getDateRange(fromDate, toDate)
  );
};

export const getInStock = (
  d: ProductDocument,
  hit?: CustomSearchServiceHits
): boolean => {
  return (
    (typeof d.inStock === 'number' && d.inStock > 0) ||
    (typeof hit?.inStock === 'number' && hit.inStock > 0) ||
    (typeof d.isInStock === 'boolean' && d.isInStock) ||
    Boolean(d.productOnline)
  );
};

export const getPricing = (d: ProductDocument) => {
  const price = typeof d.price === 'number' ? d.price : undefined;
  const specialPrice =
    typeof d.specialPrice === 'number' ? d.specialPrice : undefined;
  const oldPrice = specialPrice != null ? price : undefined;
  const discountPercent = getDiscountPercent(price, specialPrice);
  const isDiscountBadge = getDiscountDateBadge(
    price,
    specialPrice,
    d?.specialPriceFromDate,
    d?.specialPriceToDate
  );
  return {
    price: isDiscountBadge ? specialPrice : price,
    oldPrice,
    isDiscountBadge,
    discountPercent,
  };
};

export const getThumbnail = (d: ProductDocument): string => {
  if (d.thumbnail) {
    return d.thumbnail.replace(
      /\/uploads\/thumbnail_/,
      '/uploads/thumbnail-lg_'
    );
  }

  const firstImage = d?.images?.length > 0 ? d.images[0] : undefined;
  return d.thumbnail ?? firstImage ?? d.mediaGallery?.[0] ?? Logo.src;
};

export const getPromotionLabels = (d: ProductDocument): string[] => {
  return (d?.promotions ?? []).map((item) => item.promotionName!);
};

export function getMaxOrderQuantity(
  product: QuantityLimitedProduct | null | undefined
): number {
  const inventory = Number(product?.quantity);
  const perOrder = Number(product?.maxQuantityOfProduct);
  const candidates: number[] = [];
  if (Number.isFinite(inventory) && inventory > 0) candidates.push(inventory);
  if (Number.isFinite(perOrder) && perOrder > 0) candidates.push(perOrder);
  return candidates.length > 0
    ? Math.min(...candidates)
    : Number.POSITIVE_INFINITY;
}

export function exceedsMaxOrderQuantity(
  product: QuantityLimitedProduct | null | undefined,
  nextQuantity: number
): boolean {
  return nextQuantity > getMaxOrderQuantity(product);
}
